import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { IPD_COLORS, cardSx, hideScrollbarSx } from '../../theme';

export const RFQ_FORM_STEPS = [
  { key: 'details', label: 'RFQ Details', shortLabel: 'Details', icon: DescriptionOutlinedIcon,
    hint: 'Title, dates, and commercial terms' },
  { key: 'items', label: 'Line Items', shortLabel: 'Items', icon: Inventory2OutlinedIcon,
    hint: 'Products and quantities required' },
  { key: 'vendors', label: 'Invite Vendors', shortLabel: 'Vendors', icon: GroupsOutlinedIcon,
    hint: 'Select approved suppliers' },
  { key: 'review', label: 'Review & Publish', shortLabel: 'Review', icon: FactCheckOutlinedIcon,
    hint: 'Attachments and final check' },
];

export default function RfqFormStepNav({ activeStep, onStepClick, completedThrough = -1 }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return (
      <Box sx={{ mb: 2, ...cardSx, p: 1.5 }}>
        <Box sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          ...hideScrollbarSx,
        }}>
          {RFQ_FORM_STEPS.map((step, i) => {
            const active = i === activeStep;
            const done = i <= completedThrough;
            const Icon = step.icon;
            return (
              <Box
                key={step.key}
                onClick={() => onStepClick?.(i)}
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 1,
                  borderRadius: '10px',
                  cursor: onStepClick ? 'pointer' : 'default',
                  border: `1px solid ${active ? IPD_COLORS.primary : IPD_COLORS.border}`,
                  backgroundColor: active ? IPD_COLORS.selectedBg : IPD_COLORS.cardBg,
                }}
              >
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: done ? IPD_COLORS.primary : active ? IPD_COLORS.primary : IPD_COLORS.selectedBg,
                  color: done || active ? '#fff' : IPD_COLORS.primary,
                }}>
                  {done && !active ? <CheckIcon sx={{ fontSize: 16 }} /> : <Icon sx={{ fontSize: 16 }} />}
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: active ? 700 : 600, color: IPD_COLORS.textPrimary }}>
                  {step.shortLabel}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      ...cardSx,
      p: 2,
      position: 'sticky',
      top: 16,
    }}>
      <Typography sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: IPD_COLORS.textSecondary,
        mb: 2,
      }}>
        Create RFQ
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {RFQ_FORM_STEPS.map((step, i) => {
          const active = i === activeStep;
          const done = i < activeStep || i <= completedThrough;
          const Icon = step.icon;
          const isLast = i === RFQ_FORM_STEPS.length - 1;

          return (
            <Box key={step.key}>
              <Box
                onClick={() => onStepClick?.(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onStepClick?.(i);
                  }
                }}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  p: 1.25,
                  borderRadius: '10px',
                  cursor: onStepClick ? 'pointer' : 'default',
                  backgroundColor: active ? IPD_COLORS.selectedBg : 'transparent',
                  border: active ? `1px solid ${IPD_COLORS.primary}` : '1px solid transparent',
                  transition: 'background-color 0.15s ease',
                  '&:hover': onStepClick ? { backgroundColor: IPD_COLORS.selectedBg } : {},
                }}
              >
                <Box sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${active || done ? IPD_COLORS.primary : IPD_COLORS.border}`,
                  backgroundColor: done ? IPD_COLORS.primary : active ? IPD_COLORS.primary : IPD_COLORS.cardBg,
                  color: done || active ? '#fff' : IPD_COLORS.primary,
                }}>
                  {done && !active ? <CheckIcon sx={{ fontSize: 18 }} /> : <Icon sx={{ fontSize: 18 }} />}
                </Box>
                <Box sx={{ minWidth: 0, pt: 0.25 }}>
                  <Typography sx={{
                    fontSize: 13,
                    fontWeight: active ? 700 : 600,
                    color: active ? IPD_COLORS.primaryDark : IPD_COLORS.textPrimary,
                    lineHeight: 1.3,
                  }}>
                    {step.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: 11,
                    color: IPD_COLORS.textSecondary,
                    mt: 0.25,
                    lineHeight: 1.35,
                  }}>
                    {step.hint}
                  </Typography>
                </Box>
              </Box>
              {!isLast && (
                <Box sx={{
                  width: 2,
                  height: 16,
                  ml: '29px',
                  my: 0.25,
                  backgroundColor: done ? IPD_COLORS.primary : IPD_COLORS.border,
                  borderRadius: 1,
                }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
